import * as React from "react"

/** Imperative handle shared by animated sidebar icons */
export type SidebarIconAnimHandle = {
  startAnimation: () => void | Promise<void>
  stopAnimation?: () => void | Promise<void>
}

/** Icon shell: pass pointer events through to the nav link so hover/click animate the whole row */
export const sidebarNavIconShellClass =
  "pointer-events-none inline-flex shrink-0 items-center justify-center text-inherit"

/**
 * Drive icon animation from the full nav link (hover anywhere on row, pointer-down on row).
 * Pair with `sidebarNavIconShellClass` on the icon wrapper so the link receives pointer events.
 */
export function useSidebarNavIconAnimation() {
  const iconAnimRef = React.useRef<SidebarIconAnimHandle | null>(null)

  const startIconAnimation = React.useCallback(() => {
    void iconAnimRef.current?.startAnimation()
  }, [])

  const stopIconAnimation = React.useCallback(() => {
    void iconAnimRef.current?.stopAnimation?.()
  }, [])

  const linkHandlers = React.useMemo(
    () => ({
      onPointerDownCapture: startIconAnimation,
      onMouseEnter: startIconAnimation,
      onMouseLeave: stopIconAnimation,
    }),
    [startIconAnimation, stopIconAnimation]
  )

  return {
    iconAnimRef,
    linkHandlers,
    startIconAnimation,
    stopIconAnimation,
  }
}

/** Re-mount the route icon with an animation ref (createElement merges ref reliably). */
export function renderRouteIconWithAnimRef(
  icon: React.ReactNode,
  animRef: React.RefObject<SidebarIconAnimHandle | null>
): React.ReactNode {
  if (typeof icon === "string" || !React.isValidElement(icon)) {
    return icon
  }
  const el = icon as React.ReactElement<Record<string, unknown>>
  return React.createElement(el.type as React.ElementType, {
    ...el.props,
    ref: animRef,
    key: (el.key as string | number | undefined) ?? "sidebar-nav-icon",
  })
}
